/**
 * Browser side of workflow import/export: file download, clipboard, file read.
 *
 * The format itself — the bundle envelope, the flat shape, tolerance between
 * them, and id re-keying — lives in `@erp/workflows` (`portability.ts`), shared
 * with the backend so the editor's files and the local `workflow` CLI's bundles
 * are the same thing. Anything about *what* a bundle contains belongs there.
 *
 * The editor and the chat agent work in the flat shape ({@link WorkflowExportData}),
 * which is also what a proposal arrives as; files are written as bundles and
 * read as either.
 */

import { v4 as uuidv4 } from 'uuid';
import type { WorkflowDefinitionResponse } from '@erp/rpc';
import { definitionToGraph } from './definitionToGraph';
import {
  parseJsonText,
  readJsonFile,
  summarizeValidationErrors,
} from 'src/lib/readJsonFile';
import {
  bundleToFlatExport,
  flatExportToBundle,
  parseWorkflowBundle,
  remapWorkflowGraphIds,
  type FlatWorkflowExport,
} from '@erp/workflows';

/**
 * The editor's and chat agent's view of a workflow: graph plus the handful of
 * lineage fields the canvas knows about, all at the top level.
 */
export type WorkflowExportData = FlatWorkflowExport;

declare module '@tanstack/react-router' {
  // Declaration merging needs an interface.
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface HistoryState {
    /**
     * New Workflow → From JSON hands the parsed workflow to the create-mode
     * editor here rather than in the URL: it can be large, and history state
     * survives a reload of the unsaved `/workflows/new` page.
     */
    workflowImport?: WorkflowExportData;
  }
}

/** Supply definition metadata for imported files and accepted agent proposals. */
export function workflowExportToDefinition(
  data: WorkflowExportData,
): WorkflowDefinitionResponse {
  return {
    id: uuidv4(),
    workflowId: uuidv4(),
    version: 1,
    steps: data.steps,
    edges: data.edges,
    stepLayoutGroups: data.stepLayoutGroups ?? [],
    zipTies: data.zipTies ?? [],
    createdBy: '',
    parentVersionId: null,
    status: 'draft',
    isPinned: false,
    changelog: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

type ImportedNodeData = ReturnType<
  typeof definitionToGraph
>['nodes'][number]['data'];

/**
 * `hydrateStepType` must keep a dynamic step's imported sockets: imported IDs
 * are regenerated, so `graphToDefinition` serializes these nodes as new and
 * reads sockets from `_stepTypeData`. Registry sockets there would drop the
 * exported ones and orphan the edges wired to them.
 */
export function workflowExportToGraph<StepType>(
  data: WorkflowExportData,
  hydrateStepType: (nodeData: ImportedNodeData) => StepType | undefined,
) {
  const graph = definitionToGraph(workflowExportToDefinition(data));
  for (const node of graph.nodes) {
    const stepType = hydrateStepType(node.data);
    if (stepType) node.data._stepTypeData = stepType;
  }
  return graph;
}

/**
 * What an export call site passes in: the flat fields plus `version`, which
 * names the downloaded file and is deliberately not part of the format.
 *
 * Lineage settings the canvas doesn't model (activation state, component
 * settings, manager agent) are defaulted by `flatExportToBundle`. A bundle
 * exported by the CLI carries the real values; one exported from the editor
 * describes a paused, non-component workflow, which is what import should
 * create from a file a human hand-edited.
 */
export type WorkflowExportInput = WorkflowExportData & {
  /** Filename suffix only — never written into the bundle. */
  version?: number;
};

function buildExportJson(data: WorkflowExportInput): string {
  return JSON.stringify(flatExportToBundle(data), null, 2);
}

/**
 * Export a workflow definition as a downloadable JSON file.
 */
export function exportWorkflowToJson(data: WorkflowExportInput) {
  const json = buildExportJson(data);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  const safeName = data.name.replace(/[^a-zA-Z0-9_-]/g, '_');
  const versionSuffix = data.version != null ? `.v${data.version}` : '';
  a.download = `${safeName}${versionSuffix}.workflow.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Copy workflow definition JSON to the clipboard.
 */
export async function copyWorkflowJsonToClipboard(
  data: WorkflowExportInput,
): Promise<void> {
  const json = buildExportJson(data);
  await navigator.clipboard.writeText(json);
}

/**
 * Give every imported step and edge a fresh id, rewriting every reference — see
 * `remapWorkflowGraphIds` in `@erp/workflows` for exactly what moves.
 *
 * Regenerating unconditionally is safe because an import always lands in a
 * draft, and `createDraft` already re-keys every step and edge it clones
 * (`cloneStepsAndEdges`). So the file's ids are the *source* version's, which
 * still exist — keeping them is what collides. The one case they'd legitimately
 * match (export this draft, hand-edit the JSON, import it straight back) only
 * buys keeping that draft's test-run `StepRun` rows joinable, and `step_runs`
 * dropped its FK to `workflow_steps` long ago — not worth branching on.
 */
export function regenerateWorkflowIds(
  data: WorkflowExportData,
): WorkflowExportData {
  const remapped = remapWorkflowGraphIds(
    {
      ...data,
      stepLayoutGroups: data.stepLayoutGroups ?? [],
      zipTies: data.zipTies ?? [],
    },
    uuidv4,
  );
  return {
    ...data,
    steps: remapped.steps,
    edges: remapped.edges,
    // Left absent when the file had no groups, so a groups-less import stays
    // groups-less rather than gaining an empty array.
    ...(data.stepLayoutGroups
      ? { stepLayoutGroups: remapped.stepLayoutGroups }
      : {}),
    ...(data.zipTies ? { zipTies: remapped.zipTies } : {}),
  };
}

type WorkflowImportResult =
  { ok: true; data: WorkflowExportData } | { ok: false; error: string };

function validateWorkflowJson(
  raw: unknown,
  errorPrefix = 'Invalid workflow file',
): WorkflowImportResult {
  const parsed = parseWorkflowBundle(raw);
  if (!parsed.ok) {
    return {
      ok: false,
      error: `${errorPrefix}: ${summarizeValidationErrors(parsed.errors)}`,
    };
  }

  return {
    ok: true,
    data: regenerateWorkflowIds(bundleToFlatExport(parsed.bundle)),
  };
}

/**
 * Import a workflow from a JSON file. Returns validated flat data or an error
 * message. Accepts both the bundle envelope the CLI and current exports write
 * and the flat shape older exports use.
 *
 * Step and edge ids are re-keyed on the way in — see {@link regenerateWorkflowIds}.
 */
export async function importWorkflowFromJson(
  file: File,
): Promise<WorkflowImportResult> {
  const read = await readJsonFile(file);
  return read.ok ? validateWorkflowJson(read.raw) : read;
}

/**
 * {@link importWorkflowFromJson} for pasted text instead of a file, worded for
 * a paste box rather than a file picker.
 */
export function parseWorkflowJsonText(text: string): WorkflowImportResult {
  const read = parseJsonText(text);
  if (!read.ok) {
    return {
      ok: false,
      error: 'Not valid JSON — check for a missing bracket, comma, or quote.',
    };
  }
  return validateWorkflowJson(read.raw, 'Not a workflow export');
}
